import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  Signal
} from '@angular/core';
import {
  CardContainerComponent,
  closePopupData,
  createErrorPopupData,
  createSuccessPopupData,
  CurrencyFormatService,
  InformationCardComponent,
  LoaderComponent,
  LoadingButtonComponent,
  RecapCard,
  RecapCardSignalComponent,
  requiredYesNo,
  StatusMessagePopupV2Component,
  TextAreaSignalInputComponent,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface,
  UnitSignalInputComponent,
  YesNoSignalInputComponent,
  YesNoTranslation
} from '@tribeauto/ngx-mobilite-material';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ContractActionService } from '../contract-action.service';
import { ReactiveFormsModule } from '@angular/forms';
import { DEFAULT_EXEMPTION, ExemptionData } from './exemption.component.model';
import { finalize, map, Observable, startWith } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { ExemptionService } from './exemption.service';
import { ActivatedRoute } from '@angular/router';
import { toDerogationRequest } from './exemption.mapper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AUTO_CLOSE_DELAY_TEN_SECONDS } from '../../../helpers/popup.helper';
import { debounce, disabled, form, FormField, maxLength, required, validate } from '@angular/forms/signals';

@Component({
  selector: 'pvmo-derogation',
  imports: [
    LoaderComponent,
    CardContainerComponent,
    ReactiveFormsModule,
    AsyncPipe,
    LoadingButtonComponent,
    InformationCardComponent,
    TranslatePipe,
    StatusMessagePopupV2Component,
    FormField,
    TextAreaSignalInputComponent,
    UnitSignalInputComponent,
    YesNoSignalInputComponent,
    RecapCardSignalComponent
  ],
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  templateUrl: './exemption.component.html',
  styleUrl: './exemption.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExemptionComponent {
  private readonly contractActionService = inject(ContractActionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly currencyFormatService = inject(CurrencyFormatService);
  private readonly exemptionService = inject(ExemptionService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly translationConst = 'application.exemption.';
  projectInformationRecap: Signal<RecapCard> = this.contractActionService.lastFetchedProjectInformationRecapCard;
  projectRvDerogation = this.contractActionService.lastFetchedProjectRvDerogation;

  projectInformationRecapError = this.contractActionService.lastFetchedProjectInformationRecapCardError;

  yesNoTranslations$: Observable<YesNoTranslation> = this.translateService.onTranslationChange.pipe(
    startWith(''),
    map(() => ({
      yes: this.translateService.instant('application.recap-card.attribute-content.yes'),
      no: this.translateService.instant('application.recap-card.attribute-content.no')
    }))
  );

  disableRedemptionValue = signal(false);
  disableRedemptionValueGap = signal(true);
  disableForm = signal(false);

  readonly exemption = signal<ExemptionData>(DEFAULT_EXEMPTION);
  readonly exemptionForm = form(this.exemption, path => {
    disabled(path.redemptionValue, { when: () => this.disableRedemptionValue() || this.disableForm() });
    required(path.desiredRedemptionValue);
    validate(path.desiredRedemptionValue, ({ value }) =>
      value() && this.isDesiredRedemptionValueLessThanRedemptionValue(value())
        ? undefined
        : { kind: 'exemption.desired-redemption-value' }
    );
    disabled(path.desiredRedemptionValue, { when: () => this.disableForm() });
    debounce(path.desiredRedemptionValue, 500);
    disabled(path.redemptionValueGap, { when: () => this.disableRedemptionValueGap() || this.disableForm() });
    maxLength(path.exemptionReason, 250);
    disabled(path.exemptionReason, { when: () => this.disableForm() });
    requiredYesNo(path.repurchaseAgreement);
    disabled(path.repurchaseAgreement, { when: () => this.disableForm() });
  });

  id = this.activatedRoute.parent?.snapshot.paramMap.get('id');
  statusPopupElement = closePopupData(createErrorPopupData('', '', '', undefined));
  loadingSaveDerogation = signal(false);

  constructor() {
    effect(() => {
      this.exemptionForm
        .redemptionValue()
        .value.set(this.currencyFormatService.formatInputCurrency(this.contractActionService.contractResidualValue()));
      this.disableRedemptionValue.set(true);
    });

    effect(() => {
      const rvDerogation = this.projectRvDerogation();
      if (rvDerogation) {
        this.exemptionForm.repurchaseAgreement().value.set(rvDerogation.purchaseCommit);
        this.exemptionForm.desiredRedemptionValue().value.set(rvDerogation.requestedValue.toString());
        this.exemptionForm.exemptionReason().value.set(rvDerogation.description);
        this.disableForm.set(true);
      }
    });

    effect(() => {
      this.exemptionForm.redemptionValueGap().value.set(this.calculateRedemptionValueGap());
    });
  }

  submit(): void {
    if (this.id) {
      this.loadingSaveDerogation.set(true);
      this.exemptionService
        .saveDerogation(this.id, toDerogationRequest(this.exemption()))
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.loadingSaveDerogation.set(false))
        )
        .subscribe({
          next: () => {
            this.setSuccessStatusPopup();
          },
          error: () => {
            this.setErrorStatusPopup();
          }
        });
    }
  }

  closeMessagePopup(): void {
    this.statusPopupElement = closePopupData(this.statusPopupElement);
  }

  private calculateRedemptionValueGap(): string {
    return this.currencyFormatService.formatInputCurrency(
      (
        (this.currencyFormatService.currencyToNumber(this.exemptionForm.desiredRedemptionValue().value()) /
          this.currencyFormatService.currencyToNumber(this.contractActionService.contractResidualValue()) -
          1) *
        100
      ).toFixed(2)
    );
  }

  private isDesiredRedemptionValueLessThanRedemptionValue(desiredRedemptionValue: string): boolean {
    return (
      this.currencyFormatService.currencyToNumber(desiredRedemptionValue) >
      Number(this.contractActionService.contractResidualValue())
    );
  }

  private setSuccessStatusPopup(): void {
    this.statusPopupElement = createSuccessPopupData(
      this.translateService.instant(this.translationConst + 'success.title'),
      this.translateService.instant(this.translationConst + 'success.message'),
      this.translateService.instant(this.translationConst + 'success.close-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    );
    this.cdr.markForCheck();
  }

  private setErrorStatusPopup(): void {
    this.statusPopupElement = createErrorPopupData(
      this.translateService.instant(this.translationConst + 'error.title'),
      this.translateService.instant(this.translationConst + 'error.message'),
      this.translateService.instant(this.translationConst + 'error.close-label'),
      undefined
    );
    this.cdr.markForCheck();
  }
}
